import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft,
  User,
  Briefcase,
  CreditCard,
  IndianRupee,
  Building2,
  CheckCircle,
  ArrowRightLeft,
  Info,
} from "lucide-react";
import { generateEmployeeId } from "@/lib/utils";
import {
  validatePAN,
  validateAadhar,
  validateIFSC,
  validateContactPhone,
} from "@/lib/validations";
import { BLOOD_GROUP_OPTIONS } from "@/constants/employee";
import { toast } from "sonner";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";
import {
  createEmployee,
  getEarningSalaryComponents,
  getSalaryComponents,
  getCompanyMiscellaneousData,
  getEmployeeLimitStatus,
  fetchCompanyLeaveConfiguration,
} from "@/services/adminService";
import { useSubscription } from "@/contexts/SubscriptionContext";
import MultipleSelector from "@/components/common/MultiSelect";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}`;

const AddEmployee = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [limitLoading, setLimitLoading] = useState(true);
  const [shiftData, setShiftData] = useState([]);
  const { hasFeature } = useSubscription();
  const [companyLeaves, setCompanyLeaves] = useState([]);
  const [accessRole, setAccessRole] = useState([]);

  const showDepartmentDesignation = hasFeature("department_mapping");

  // Check employee limit on component mount
  useEffect(() => {
    const checkLimit = async () => {
      try {
        const response = await getEmployeeLimitStatus();
        // If limit reached, show warning and redirect
        if (!response.allowed) {
          toast.error(response.message);
          setTimeout(() => navigate("/employees"), 2000);
        }
      } catch (error) {
        console.error("Error checking employee limit:", error);
      } finally {
        setLimitLoading(false);
      }
    };
    checkLimit();
  }, [navigate]);

  const [availableComponents, setAvailableComponents] = useState([]);
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [workingLocations, setWorkingLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designation, setDesignation] = useState([]);
  const [formData, setFormData] = useState({
    // Personal Information
    employee_id: generateEmployeeId(),
    name: "",
    email: "",
    phone: "",
    gender: "",
    date_of_birth: "",
    aadhar_number: "",
    pan_number: "",
    marital_status: "",
    address: "",

    // Emergency Contact & Medical Information
    emergency_contact_name: "",
    emergency_contact_phone: "",
    blood_group: "",

    // Job Information
    department: "",
    designation: "",
    date_of_joining: "",
    work_location: "",
    access_role: "",
    status: "active",
    is_director: false,
    requires_daily_login: false,

    // Bank Information
    bank_info: {
      bank_name: "",
      account_number: "",
      ifsc_code: "",
      branch: "",
    },

    // Salary Structure
    salary_structure: {
      basic_salary: "",
      house_rent_allowance: "",
      medical_allowance: "",
      leave_travel_allowance: "",
      conveyance_allowance: "",
      performance_incentive: "",
      other_benefits: "",
      pf_employee: "",
      pf_employer: "",
      esi_employee: "",
      esi_employer: "",
      professional_tax: "",
      tds: "",
      loan_deductions: "",
      others: "",
    },

    // Leave Configuration
    // NOTE: is_on_probation is NOT user-editable and NOT derived on the frontend.
    // The backend is the single source of truth and recomputes it from
    // probation_end_date. This default is kept only for payload backward-compatibility.
    is_on_probation: true,
    probation_end_date: "",

    //shifts
    shift_id: "",
    shift_name: "",
    shift_timings: "",
    leaves: [],
  });
  const [leaveBalances, setLeaveBalances] = useState({});
  const [checkinOutEnabled, setCheckinOutEnabled] = useState(false);

  const leaveOptions = companyLeaves
    ?.filter((l) => !l.is_system_generated)
    .map((l) => ({
      label: l.leave_name,
      value: l._id,
    }));

  const [errors, setErrors] = useState({});
  const steps = [
    { id: 1, title: "Personal Info", icon: User },
    { id: 2, title: "Job Info", icon: Briefcase },
    { id: 3, title: "Bank Info", icon: Building2 },
    { id: 4, title: "Salary Structure", icon: IndianRupee },
    { id: 5, title: "Review", icon: CheckCircle },
  ];

  // Fetch available salary components when reaching salary step
  useEffect(() => {
    if (currentStep === 2) {
      fetchCompanyMiscellaneousData();
    }
    if (currentStep === 4) {
      fetchSalaryComponents();
    }
  }, [currentStep]);

  useEffect(() => {
    fetchLeaveConfiguratons();
  }, []);

  const fetchSalaryComponents = async () => {
    try {
      // Fetch earnings & deductions
      const earningsRes = await getEarningSalaryComponents("earnings");
      const deductionsRes = await getSalaryComponents("deductions");

      const earnings = (earningsRes.components || []).filter(
        (c) => c.is_active && c.part_of_salary_structure,
      );

      const deductions = (deductionsRes.components || []).filter(
        (c) => c.is_active,
      );

      // Filter out already selected items
      const filterEarnings = earnings.filter(
        (c) =>
          !selectedComponents.find(
            (sc) => sc._id.toString() === c._id.toString()
          ),
      );

      const filterDeductions = deductions.filter(
        (c) =>
          !selectedComponents.find(
            (sc) => sc._id.toString() === c._id.toString()
          ),
      );

      setAvailableComponents({
        earnings: filterEarnings,
        deductions: filterDeductions,
      });
    } catch (error) {
      console.error("Error fetching salary components:", error);
      toast.error("Failed to load salary components");
    }
  };

  const fetchLeaveConfiguratons = async () => {
    try {
      const { data: res } = await fetchCompanyLeaveConfiguration();
      console.log(res);
      const list = res?.leave_config || [];
      if (Array.isArray(list)) {
        const slimList = list.map((item) => ({
          _id: item._id,
          leave_name: item.leave_name,
          is_system_generated: item.is_system_generated,
          total_allocated: item.accrual_days,
          carry_forwarded: item.carry_forward_days,
        }));

        setCompanyLeaves(slimList);
      }
    } catch (err) {
      console.error("Failed to fetch leave configuration", err);
    }
  };

  const handleComponentToggle = (component) => {
    console.log("component------------", component);

    setSelectedComponents((prev) => {
      if (!Array.isArray(prev)) prev = [];

      const exists = prev.some((c) => c._id === component._id);

      if (exists) {
        // REMOVE ONE — safe
        return prev.filter((c) => c._id !== component._id);
      }

      // ADD ONE — safe
      return [
        ...prev,
        {
          _id: component._id,
          component_id: component._id,
          component_name:
            component.component_name || component.name_in_payslip || "",
          component_type: component.component_type,
          category: component.category,
          name_in_payslip: component.name_in_payslip,
          amount: 0,
          is_active: true,
        },
      ];
    });
  };

  const handleComponentAmountChange = (componentId, amount) => {
    setSelectedComponents(
      selectedComponents.map((c) =>
        c._id === componentId ? { ...c, amount: parseFloat(amount) || 0 } : c
      )
    );
  };

  const handleInputChange = (
    field,
    value,
    nested = null,
    uppercase = false
  ) => {
    if (uppercase) {
      value = value.toUpperCase();
    }

    if (nested) {
      setFormData((prev) => ({
        ...prev,
        [nested]: {
          ...prev[nested],
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear validation error
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleAadharChange = (value) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, "");

    // Limit to 12 digits max
    const trimmed = digitsOnly.slice(0, 12);

    // Add dashes after every 4 digits
    const formatted = trimmed.replace(/(\d{4})(?=\d)/g, "$1-");

    setFormData((prev) => ({ ...prev, aadhar_number: formatted }));
  };

  const fetchCompanyMiscellaneousData = async () => {
    try {
      const response = await getCompanyMiscellaneousData();
      console.log("fetchCompanyMiscellaneousData", response);
      setWorkingLocations(response.work_locations);
      setDepartments(response.departments);
      setDesignation(response.designations);
      setShiftData(response.shifts);
      setAccessRole(response.accessRoles);
      setCheckinOutEnabled(response.isCheckinOutEnabled);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to load company configuration"));
      }
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1: // Personal Info
        if (!formData.name?.trim()) newErrors.name = "Name is required";
        if (!formData.email?.trim()) newErrors.email = "Email is required";
        if (!formData.phone?.trim()) newErrors.phone = "Phone is required";
        if (!formData.gender) newErrors.gender = "Gender is required";
        if (!formData.date_of_birth)
          newErrors.date_of_birth = "Date of birth is required";

        if (!formData.aadhar_number?.trim()) {
          newErrors.aadhar_number = "Aadhar number is required";
        } else if (!validateAadhar(formData.aadhar_number)) {
          newErrors.aadhar_number = "Invalid Aadhar number format";
        }

        if (!formData.pan_number?.trim()) {
          newErrors.pan_number = "PAN number is required";
        } else if (!validatePAN(formData.pan_number)) {
          newErrors.pan_number = "Invalid PAN number format";
        }

        if (!formData.marital_status)
          newErrors.marital_status = "Marital status is required";
        if (!formData.address?.trim())
          newErrors.address = "Address is required";

        // Emergency details are optional — only the format is checked, and
        // only when a value was actually entered.
        if (
          formData.emergency_contact_phone?.trim() &&
          !validateContactPhone(formData.emergency_contact_phone)
        ) {
          newErrors.emergency_contact_phone =
            "Invalid emergency contact phone number";
        }
        break;

      case 2: // Job Info
        if (showDepartmentDesignation) {
          if (!formData.department?.trim())
            newErrors.department = "Department is required";
          if (!formData.designation?.trim())
            newErrors.designation = "Designation is required";
        }
        if (!formData.shift_id) newErrors.shift_id = "Shift is required";
        if (!formData.date_of_joining)
          newErrors.date_of_joining = "Date of joining is required";
        if (!formData.work_location?.trim())
          newErrors.work_location = "Work location is required";
        // Probation status is derived by the backend from probation_end_date.
        // The frontend no longer controls or validates it.
        break;

      case 3: // Bank Info
        if (!formData.bank_info?.bank_name?.trim())
          newErrors.bank_name = "Bank name is required";
        if (!formData.bank_info?.branch?.trim())
          newErrors.branch = "Branch name is required";
        if (!formData.bank_info?.account_number?.trim())
          newErrors.account_number = "Account number is required";

        if (!formData.bank_info?.ifsc_code?.trim()) {
          newErrors.ifsc_code = "IFSC code is required";
        } else if (!validateIFSC(formData.bank_info.ifsc_code)) {
          newErrors.ifsc_code = "Invalid IFSC code format";
        }
        break;

      case 4: // Salary Structure
        const earningsSelected = selectedComponents.filter(
          (c) => c.category !== "deductions"
        );

        if (earningsSelected.length === 0 && formData.status === "active") {
          newErrors.salary_structure =
            "Please select at least one earning component";
          toast.error("Please select at least one earning component");
        } else {
          const missingAmounts = earningsSelected.filter(
            (c) =>
              c.amount === null || c.amount === undefined || c.amount === ""
          );

          if (missingAmounts.length > 0 && formData.status === "active") {
            newErrors.salary_structure =
              "Please enter amount for all selected earnings (can be 0)";
            toast.error(
              "Please enter amount for all selected earnings (can be 0)"
            );
          }
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    setLoading(true);
    try {
      const sanitizedRoot = {
        ...formData,
        status: formData.status,
        gender: formData.gender,
        access_role: formData.access_role,
        marital_status: formData.marital_status
          ? formData.marital_status
          : null,
      };

      // Prepare data with component-based salary structure
      const earningsTotal = selectedComponents
        .filter((item) => item.category !== "deductions")
        .reduce((sum, component) => sum + (Number(component.amount) || 0), 0);

      const deductionsTotal = selectedComponents
        .filter((item) => item.category === "deductions")
        .reduce((sum, component) => sum + (Number(component.amount) || 0), 0);

      const processedLeaveBalances =
      Array.isArray(formData.leaves) && formData.leaves.length > 0
        ? formData.leaves
            .map((leaveId) => {
              const leave = companyLeaves.find((l) => l._id === leaveId);
              if (!leave || leave.is_system_generated) return null; // skip unpaid

              const value = leaveBalances[leaveId];
              if (value === undefined || value === null) return null;

              return {
                leave_id: leaveId,
                isEdited: value.isEdited || false,
                opening_balance: value || 0,
              };
            })
            .filter(Boolean)
        : [];

      const processedData = {
        ...sanitizedRoot,
        salary_structure:
          selectedComponents.length > 0
            ? selectedComponents.map((item) => ({
                component_id: item._id || item.component_id,
                component_name:
                  item?.component_name || item?.name_in_payslip || "",
                component_type: item.component_type,
                category: item.category || "",
                name_in_payslip: item.name_in_payslip,
                amount: item.amount,
                is_active: item.is_active,
              }))
            : [],
        gross_salary: earningsTotal,
        net_salary: earningsTotal - deductionsTotal,
        shift_id: formData.shift_id,
        shift_name: formData.shift_name,
        shift_timings: formData.shift_timings,
        manual_leave_balance: processedLeaveBalances,
      };

      const response = await createEmployee(processedData);
      toast.success("Employee added successfully!");
      navigate("/employees");
    } catch (error) {
      console.error("Error creating employee:", error);
      const errorMessage = getErrorMessage(error, "Failed to create employee");
      toast.error(error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="employee_id">Employee ID</Label>
                <Input
                  id="employee_id"
                  value={formData.employee_id}
                  onChange={(e) =>
                    handleInputChange("employee_id", e.target.value, null, true)
                  }
                  className="bg-gray-50"
                  placeholder="Auto-generated"
                />
              </div>

              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={errors.name ? "border-red-500" : ""}
                  data-testid="name-input"
                  placeholder="Enter full name"
                />
                {errors.name && (
                  <p className="text-sm text-red-500 mt-1">{errors.name}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className={errors.email ? "border-red-500" : ""}
                  data-testid="email-input"
                  placeholder="Enter email"
                />
                {errors.email && (
                  <p className="text-sm text-red-500 mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  className={errors.phone ? "border-red-500" : ""}
                  data-testid="phone-input"
                  placeholder="Enter phone number"
                />
                {errors.phone && (
                  <p className="text-sm text-red-500 mt-1">{errors.phone}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="gender">Gender *</Label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) => handleInputChange("gender", value)}
                >
                  <SelectTrigger
                    className={errors.gender ? "border-red-500" : ""}
                  >
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {errors.gender && (
                  <p className="text-sm text-red-500 mt-1">{errors.gender}</p>
                )}
              </div>

              <div>
                <Label htmlFor="date_of_birth">Date of Birth *</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  max={new Date().toISOString().split("T")[0]}
                  value={formData.date_of_birth}
                  onChange={(e) =>
                    handleInputChange("date_of_birth", e.target.value)
                  }
                  className={errors.date_of_birth ? "border-red-500" : ""}
                  placeholder="Select date"
                />
                {errors.date_of_birth && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.date_of_birth}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="marital_status">Marital Status *</Label>
                <Select
                  value={formData.marital_status}
                  onValueChange={(value) =>
                    handleInputChange("marital_status", value)
                  }
                >
                  <SelectTrigger
                    className={errors.marital_status ? "border-red-500" : ""}
                  >
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="married">Married</SelectItem>
                    <SelectItem value="divorced">Divorced</SelectItem>
                    <SelectItem value="widowed">Widowed</SelectItem>
                  </SelectContent>
                </Select>
                {errors.marital_status && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.marital_status}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="marital_status">Employee Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: value.toLowerCase(),
                    }))
                  }
                >
                  <SelectTrigger
                    className={errors.status ? "border-red-500" : ""}
                  >
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>

                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="resigned">Resigned</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                  </SelectContent>
                </Select>
                {errors.status && (
                  <p className="text-sm text-red-500 mt-1">{errors.status}</p>
                )}
              </div>

              <div>
                <Label htmlFor="aadhar_number">Aadhar Number *</Label>
                <Input
                  id="aadhar_number"
                  value={formData.aadhar_number}
                  onChange={(e) => handleAadharChange(e.target.value)}
                  placeholder="Enter Aadhar Number"
                  className={errors.aadhar_number ? "border-red-500" : ""}
                />
                {errors.aadhar_number && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.aadhar_number}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="pan_number">PAN Number *</Label>
                <Input
                  id="pan_number"
                  value={formData.pan_number}
                  onChange={(e) =>
                    handleInputChange(
                      "pan_number",
                      e.target.value.toUpperCase()
                    )
                  }
                  placeholder="Enter PAN Number"
                  className={errors.pan_number ? "border-red-500" : ""}
                />
                {errors.pan_number && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.pan_number}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="address">Address *</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                className={errors.address ? "border-red-500" : ""}
                rows={3}
                placeholder="Enter full residential address"
              />
              {errors.address && (
                <p className="text-sm text-red-500 mt-1">{errors.address}</p>
              )}
            </div>

            {/* Emergency Contact & Medical Information */}
            <Separator />
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Emergency Contact & Medical Information
            </h3>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="emergency_contact_name">
                  Emergency Contact Name
                </Label>
                <Input
                  id="emergency_contact_name"
                  value={formData.emergency_contact_name}
                  onChange={(e) =>
                    handleInputChange("emergency_contact_name", e.target.value)
                  }
                  placeholder="Enter contact name"
                />
              </div>

              <div>
                <Label htmlFor="emergency_contact_phone">
                  Emergency Contact Phone
                </Label>
                <Input
                  id="emergency_contact_phone"
                  value={formData.emergency_contact_phone}
                  onChange={(e) =>
                    handleInputChange("emergency_contact_phone", e.target.value)
                  }
                  placeholder="Enter contact phone"
                  className={
                    errors.emergency_contact_phone ? "border-red-500" : ""
                  }
                />
                {errors.emergency_contact_phone && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.emergency_contact_phone}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="blood_group">Blood Group</Label>
                <Select
                  value={formData.blood_group}
                  onValueChange={(value) =>
                    handleInputChange("blood_group", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select blood group" />
                  </SelectTrigger>
                  <SelectContent>
                    {BLOOD_GROUP_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            {showDepartmentDesignation && (
              <div className="grid grid-cols-2 gap-4">
                {/* Department */}
                <div>
                  <Label htmlFor="department">Department *</Label>
                  <Select
                    value={formData.department}
                    onValueChange={(value) =>
                      handleInputChange("department", value)
                    }
                  >
                    <SelectTrigger
                      className={errors.department ? "border-red-500" : ""}
                    >
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>

                    <SelectContent>
                      {departments?.map((dept, index) => (
                        <SelectItem key={index} value={dept.name}>
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {errors.department && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.department}
                    </p>
                  )}
                </div>

                {/* Designation */}
                <div>
                  <Label htmlFor="designation">Designation *</Label>
                  <Select
                    value={formData.designation}
                    onValueChange={(value) =>
                      handleInputChange("designation", value)
                    }
                  >
                    <SelectTrigger
                      className={errors.designation ? "border-red-500" : ""}
                    >
                      <SelectValue placeholder="Select designation" />
                    </SelectTrigger>

                    <SelectContent>
                      {designation?.map((des, index) => (
                        <SelectItem key={index} value={des.name}>
                          {des.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {errors.designation && (
                    <p className="text-sm text-red-500 mt-1">
                      {errors.designation}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="mt-4">
              <div
                className={`
                    group relative flex items-start gap-4 p-4 rounded-xl border transition-all duration-200
                    ${
                      formData.is_director
                        ? "bg-indigo-50/70 border-indigo-200 dark:bg-indigo-500/10 dark:border-indigo-500/30"
                        : "bg-background border-border hover:bg-muted/40"
                    }
                  `}
              >
                {/* Checkbox */}
                <div className="flex items-start pt-0.5">
                  <Checkbox
                    id="is_director"
                    className="size-5"
                    checked={formData.is_director}
                    onCheckedChange={(checked) =>
                      handleInputChange("is_director", Boolean(checked))
                    }
                  />
                </div>

                {/* Content */}
                <label
                  htmlFor="is_director"
                  className="flex flex-col gap-1 cursor-pointer select-none"
                >
                  <span
                    className={`text-sm font-semibold leading-none transition-colors ${
                      formData.is_director
                        ? "text-indigo-900 dark:text-indigo-300"
                        : "text-foreground"
                    }`}
                  >
                    Company Director
                  </span>

                  <span className="text-xs leading-relaxed text-muted-foreground">
                    Applies director-specific compliance rules such as{" "}
                    <span className="font-medium text-foreground/70">
                      PF, ESI, TDS exemptions, and signing authority
                    </span>
                    .
                  </span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Shift *</Label>

                <Select
                  value={formData.shift_id}
                  onValueChange={(shiftId) => {
                    const selectedShift = shiftData.find(
                      (s) => s._id === shiftId,
                    );
                    handleInputChange("shift_id", shiftId);

                    // Optional extra data
                    handleInputChange("shift_name", selectedShift?.name);
                    handleInputChange(
                      "shift_timings",
                      selectedShift?.startTime + " - " + selectedShift?.endTime,
                    );
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Shift" />
                  </SelectTrigger>

                  <SelectContent>
                    {shiftData?.length === 0 ? (
                      <SelectItem disabled value="--">
                        No shifts found
                      </SelectItem>
                    ) : (
                      shiftData.map((shift) => (
                        <SelectItem key={shift._id} value={shift._id}>
                          {shift.name} ({shift.startTime} - {shift.endTime})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>

                {errors.shift_id && (
                  <p className="text-sm text-red-500 mt-1">{errors.shift_id}</p>
                )}
              </div>

              {checkinOutEnabled && (
                <div
                  className="flex items-center space-x-2 bg-orange-50/70 border-orange-200
                  dark:bg-gray-900 dark:border-gray-700 p-4 rounded-lg border"
                >
                  <Checkbox
                    id="requires_daily_login"
                    checked={formData.requires_daily_login || false}
                    onCheckedChange={(checked) =>
                      //   setFormData({ ...formData, requires_daily_login: !!checked })
                      handleInputChange("requires_daily_login", !!checked)
                    }
                  />
                  <Label
                    htmlFor="requires_daily_login"
                    className="text-sm font-medium cursor-pointer"
                  >
                    Is Employee Required to Log In Daily
                  </Label>
                </div>
              )}

              <div>
                <Label htmlFor="work_location">Work Location *</Label>
                <Select
                  value={formData.work_location}
                  onValueChange={(value) =>
                    handleInputChange("work_location", value)
                  }
                >
                  <SelectTrigger
                    className={errors.work_location ? "border-red-500" : ""}
                  >
                    <SelectValue placeholder="Select work location" />
                  </SelectTrigger>

                  <SelectContent>
                    {workingLocations?.map((loc, index) => (
                      <SelectItem key={index} value={loc.name}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {errors.work_location && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.work_location}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="date_of_joining">Date of Joining *</Label>
                <Input
                  id="date_of_joining"
                  type="date"
                  value={formData.date_of_joining}
                  onChange={(e) =>
                    handleInputChange("date_of_joining", e.target.value)
                  }
                  className={errors.date_of_joining ? "border-red-500" : ""}
                />
                {errors.date_of_joining && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.date_of_joining}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="probation_end_date"> Probation End Date </Label>
                <Input
                  id="probation_end_date"
                  type="date"
                  value={formData.probation_end_date}
                  onChange={(e) =>
                    handleInputChange("probation_end_date", e.target.value)
                  }
                />
                {errors.probation_end_date && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.probation_end_date}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Assign Leaves *</Label>

                <MultipleSelector
                  value={formData.leaves}
                  onChange={(selectedIds) => {
                    const finalLeaves = selectedIds.map(String);

                    setFormData((prev) => ({
                      ...prev,
                      leaves: finalLeaves,
                    }));

                    setLeaveBalances((prev) => {
                      const next = {};

                      finalLeaves.forEach((leaveId) => {
                        const companyLeave = companyLeaves.find(
                          (l) => String(l._id) === leaveId,
                        );

                        // Skip system-generated leaves
                        if (!companyLeave || companyLeave.is_system_generated)
                          return;

                        next[leaveId] = {
                          total_allocated: 0,
                          carry_forwarded: 0,
                          isEdited: false,
                        };
                      });

                      return next;
                    });
                  }}
                  options={leaveOptions}
                  placeholder="Select leave types"
                />

                {formData.leaves?.length > 0 && (
                  <div className="space-y-2 p-4 rounded-md border bg-gray-50 dark:bg-gray-900 dark:border-gray-700">
                    <h4 className="font-semibold text-sm">
                      Existing Leave Balances (optional)
                    </h4>

                    <div className="space-y-2">
                      {formData.leaves.map((leaveId) => {
                        const leave = companyLeaves.find(
                          (l) => l._id === leaveId && !l.is_system_generated,
                        );
                        if (!leave) return null;

                        return (
                          <div
                            key={leaveId}
                            className="flex items-center justify-between px-3 py-4 rounded-lg border bg-white dark:bg-gray-800 dark:border-gray-700"
                          >
                            <div className="flex items-center gap-4 min-w-0">
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
                                  {leave.leave_name}
                                </p>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">
                                  Active Allocation
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-4 self-end sm:self-center">
                              <div className="flex flex-col items-end">
                                <div className="relative flex items-center gap-2">
                                  <Input
                                    type="number"
                                    className="w-20 sm:w-24 text-center font-black text-indigo-600 dark:text-indigo-400 [&::-webkit-inner-spin-button]:appearance-none bg-slate-50/50 dark:bg-slate-900 h-9 text-base"
                                    value={
                                      leaveBalances[leaveId]?.total_allocated ||
                                      0
                                    }
                                    min={0}
                                    onWheel={(e) => e.currentTarget.blur()}
                                    onChange={(e) => {
                                      const value = Number(e.target.value) || 0;

                                      setLeaveBalances((prev) => ({
                                        ...prev,
                                        [leaveId]: {
                                          ...prev[leaveId],
                                          total_allocated: value,
                                          isEdited: value !== 0 ? true : false,
                                        },
                                      }));
                                    }}
                                  />

                                  <div className="absolute -top-6 right-0">
                                    <span className="text-[8px] font-black text-indigo-400 uppercase tracking-widest">
                                      Total
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {leave.carry_forwarded > 0 && (
                              <div className="flex items-center gap-3 border-l dark:border-slate-700 pl-4">
                                <ArrowRightLeft className="w-3.5 h-3.5 text-slate-300 sm:block" />
                                <div className="flex flex-col items-end">
                                  <div className="relative flex items-center gap-2">
                                    <Input
                                      type="number"
                                      className="w-20 sm:w-24 text-center font-bold h-9"
                                      min={0}
                                      value={
                                        leaveBalances[leaveId]
                                          ?.carry_forwarded || 0
                                      }
                                      onWheel={(e) => e.currentTarget.blur()}
                                      onChange={(e) => {
                                        const raw = Number(e.target.value);
                                        const max =
                                          leaveBalances[leaveId]
                                            .total_allocated;

                                        const value = Number.isNaN(raw)
                                          ? 0
                                          : Math.min(Math.max(raw, 0), max);

                                        setLeaveBalances((prev) => ({
                                          ...prev,
                                          [leaveId]: {
                                            ...prev[leaveId],
                                            carry_forwarded: value,
                                            isEdited:
                                              value !== 0 ? true : false,
                                          },
                                        }));
                                      }}
                                    />

                                    <div className="absolute -top-6 right-0">
                                      <span className="text-[8px] font-black text-indigo-400 uppercase tracking-widest">
                                        Carried
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <div className="flex items-center space-x-2 bg-blue-50 dark:bg-blue-500/30 p-2 rounded-lg border border-blue-200">
                        <Info className="w-4 h-4 text-blue-500" />
                        <Label className="text-xs cursor-pointer">
                          Total leave balance includes both allocated and
                          carried-forward leaves.
                        </Label>
                      </div>
                    </div>
                  </div>
                )}

                {errors.leaves && (
                  <p className="text-sm text-red-500 mt-1">{errors.leaves}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="bank_name">Bank Name *</Label>
                <Input
                  id="bank_name"
                  value={formData.bank_info.bank_name}
                  onChange={(e) =>
                    handleInputChange("bank_name", e.target.value, "bank_info")
                  }
                  placeholder="Enter Bank Name"
                  className={errors.bank_name ? "border-red-500" : ""}
                />
                {errors.bank_name && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.bank_name}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="branch">Branch</Label>
                <Input
                  id="branch"
                  value={formData.bank_info.branch}
                  onChange={(e) =>
                    handleInputChange("branch", e.target.value, "bank_info")
                  }
                  placeholder="Enter Branch Name"
                />
                {errors.branch && (
                  <p className="text-sm text-red-500 mt-1">{errors.branch}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="account_number">Account Number *</Label>
                <Input
                  id="account_number"
                  value={formData.bank_info.account_number}
                  onChange={(e) =>
                    handleInputChange(
                      "account_number",
                      e.target.value,
                      "bank_info"
                    )
                  }
                  className={errors.account_number ? "border-red-500" : ""}
                  placeholder="Enter Account Number"
                />
                {errors.account_number && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.account_number}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="ifsc_code">IFSC Code *</Label>
                <Input
                  id="ifsc_code"
                  value={formData.bank_info.ifsc_code}
                  onChange={(e) =>
                    handleInputChange(
                      "ifsc_code",
                      e.target.value.toUpperCase(),
                      "bank_info"
                    )
                  }
                  placeholder="Enter IFSC Code"
                  className={errors.ifsc_code ? "border-red-500" : ""}
                />
                {errors.ifsc_code && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.ifsc_code}
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 4: {
        const totalEarnings = selectedComponents
          .filter((c) => c.category !== "deductions")
          .reduce((sum, c) => sum + (c.amount || 0), 0);

        const totalDeductions = selectedComponents
          .filter((c) => c.category === "deductions")
          .reduce((sum, c) => sum + (c.amount || 0), 0);

        return (
          <div className="space-y-8">
            {/* Earnings Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <IndianRupee className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    Earnings
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Add salary components for this employee
                  </p>
                </div>
                <Select
                  value=""
                  placeholder="Add Earnings"
                  onValueChange={(value) => {
                    const component = availableComponents.earnings.find(
                      (c) => c._id === value
                    );
                    if (component) {
                      handleComponentToggle(component);
                    }
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="+ Add Earning" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableComponents.earnings &&
                    availableComponents.earnings.length > 0 ? (
                      <>
                        {availableComponents.earnings
                          .filter(
                            (c) =>
                              !selectedComponents.find((sc) => sc._id === c._id)
                          )
                          .map((component) => (
                            <SelectItem
                              key={component._id}
                              value={component._id}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {component.name_in_payslip}
                                </span>
                                <span className="text-xs">
                                  {component.component_type}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        {availableComponents.earnings.filter(
                          (c) =>
                            !selectedComponents.find((sc) => sc._id === c._id)
                        ).length === 0 && (
                          <SelectItem value="placeholder" disabled>
                            All components added
                          </SelectItem>
                        )}
                      </>
                    ) : (
                      <SelectItem value="placeholder" disabled>
                        No components available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Selected Earnings */}
              <div className="space-y-2">
                {selectedComponents.filter((c) => c.category !== "deductions")
                  .length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed rounded-lg bg-gray-50/50 dark:bg-gray-800/50">
                    <p className="text-gray-500 dark:text-gray-400">
                      No earnings added yet
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                      Click "Add Earning" to get started
                    </p>
                  </div>
                ) : (
                  selectedComponents
                    .filter((c) => c.category !== "deductions")
                    .map((component) => (
                      <div
                        key={component._id}
                        className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 border rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {component.name_in_payslip}
                          </div>
                          <div className="text-xs ">{component.category}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center">
                            <span className="text-gray-500 mr-2">₹</span>
                            <Input
                              type="number"
                              onWheel={(e) => e.target.blur()}
                              placeholder="0"
                              className="w-32 text-right"
                              value={component.amount || ""}
                              onChange={(e) =>
                                handleComponentAmountChange(
                                  component._id,
                                  e.target.value
                                )
                              }
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                            onClick={() => handleComponentToggle(component)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3 6h18"></path>
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                            </svg>
                          </Button>
                        </div>
                      </div>
                    ))
                )}
              </div>

              {/* Earnings Total */}
              {selectedComponents.filter((c) => c.category !== "deductions")
                .length > 0 && (
                <div className="flex justify-end items-center gap-2 px-4 py-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    Total Earnings:
                  </span>
                  <span className="text-xl font-bold text-green-600 dark:text-green-400">
                    ₹{totalEarnings.toLocaleString()}
                  </span>
                </div>
              )}
            </div>

            <Separator className="my-6" />

            {/* Deductions Section */}
            {hasFeature("advanced_payroll") && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                      </div>
                      Deductions
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Add non-tax deductions (EPF, ESI, TDS, PT will be
                      auto-calculated)
                    </p>
                  </div>
                  <Select
                    value=""
                    onValueChange={(value) => {
                      const component = availableComponents.deductions.find(
                        (c) => c._id === value
                      );
                      if (component) {
                        handleComponentToggle(component);
                      }
                    }}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="+ Add Deduction" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableComponents.deductions &&
                      availableComponents.deductions.length > 0 ? (
                        <>
                          {availableComponents.deductions
                            .filter(
                              (c) =>
                                !selectedComponents.find(
                                  (sc) => sc._id === c._id
                                )
                            )
                            .map((component) => (
                              <SelectItem
                                key={component._id}
                                value={component._id}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {component.name_in_payslip}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {component.category}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          {availableComponents.deductions.filter(
                            (c) =>
                              !selectedComponents.find((sc) => sc._id === c._id)
                          ).length === 0 && (
                            <SelectItem value="placeholder" disabled>
                              All components added
                            </SelectItem>
                          )}
                        </>
                      ) : (
                        <SelectItem value="placeholder" disabled>
                          No components available
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Selected Deductions */}
                <div className="space-y-2">
                  {selectedComponents.filter((c) => c.category === "deductions")
                    .length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed rounded-lg bg-gray-50/50 dark:bg-gray-800/50">
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        No manual deductions added
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        Amounts will be configured during payroll
                      </p>
                    </div>
                  ) : (
                    selectedComponents
                      .filter((c) => c.category === "deductions")
                      .map((component) => (
                        <div
                          key={component._Id}
                          className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 border rounded-lg hover:shadow-md transition-shadow"
                        >
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {component.name_in_payslip}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {component.category}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center">
                              <span className="text-gray-500 mr-2">₹</span>
                              <Input
                                type="number"
                                onWheel={(e) => e.target.blur()}
                                placeholder="0"
                                className="w-32 text-right"
                                value={component.amount || ""}
                                onChange={(e) =>
                                  handleComponentAmountChange(
                                    component._id,
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                              onClick={() => handleComponentToggle(component)}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M3 6h18"></path>
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                              </svg>
                            </Button>
                          </div>
                        </div>
                      ))
                  )}
                </div>
                {/* Earnings Total */}
                {selectedComponents.filter((c) => c.category !== "deductions")
                  .length > 0 && (
                  <div className="flex justify-end items-center gap-2 px-4 py-3 bg-green-50 dark:bg-red-900/20 rounded-lg">
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      Total Deductions:
                    </span>
                    <span className="text-xl font-bold text-green-600 dark:text-red-400">
                      ₹{totalDeductions.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Net Salary Summary */}
            {selectedComponents.filter((c) => c.category === "earnings")
              .length > 0 && (
              <div className="mt-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Gross Monthly Salary
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      Deductions (including EPF, ESI, TDS, PT, Loans) will be
                      calculated during payroll
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                      ₹{totalEarnings - totalDeductions}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Total Earnings
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }

      case 5: {
        const grossSalary = selectedComponents
          .filter((c) => c.category !== "deductions")
          .reduce((sum, c) => sum + (c.amount || 0), 0);

        const totalDeductions = selectedComponents
          .filter((c) => c.category === "deductions")
          .reduce((sum, c) => sum + (c.amount || 0), 0);

        const netSalary = grossSalary - totalDeductions;

        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-primary mb-2">
                Review Employee Details
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Please review all information before submitting
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {/* PERSONAL INFO */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    Personal Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div>
                    <strong>Employee ID:</strong> {formData.employee_id}
                  </div>
                  <div>
                    <strong>Name:</strong> {formData.name}
                  </div>
                  <div>
                    <strong>Email:</strong> {formData.email}
                  </div>
                  <div>
                    <strong>Phone:</strong> {formData.phone}
                  </div>
                  <div>
                    <strong>Gender:</strong> {formData.gender}
                  </div>
                  <div>
                    <strong>Date of Birth:</strong> {formData.date_of_birth}
                  </div>
                  <div>
                    <strong>Aadhar:</strong> {formData.aadhar_number}
                  </div>
                  <div>
                    <strong>PAN:</strong> {formData.pan_number}
                  </div>
                  <div>
                    <strong>Marital Status:</strong> {formData.marital_status}
                  </div>
                  <div>
                    <strong>Address:</strong> {formData.address}
                  </div>
                </CardContent>
              </Card>

              {/* JOB INFO */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Job Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {showDepartmentDesignation && (
                    <>
                      <div>
                        <strong>Department:</strong> {formData.department}
                      </div>
                      <div>
                        <strong>Designation:</strong> {formData.designation}
                      </div>
                    </>
                  )}

                  <div>
                    <strong>Shift:</strong> {formData.shift_name}
                  </div>
                  <div>
                    <strong>Date of Joining:</strong> {formData.date_of_joining}
                  </div>
                  <div>
                    <strong>Work Location:</strong> {formData.work_location}
                  </div>
                  {/* Probation status is determined by the backend from this date,
                      so only the admin-entered date is shown here. */}
                  {formData.probation_end_date && (
                    <div>
                      <strong>Probation End Date:</strong>{" "}
                      {formData.probation_end_date}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* BANK INFO */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Bank Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div>
                    <strong>Bank Name:</strong> {formData.bank_info.bank_name}
                  </div>
                  <div>
                    <strong>Branch:</strong> {formData.bank_info.branch}
                  </div>
                  <div>
                    <strong>Account Number:</strong>{" "}
                    {formData.bank_info.account_number}
                  </div>
                  <div>
                    <strong>IFSC Code:</strong> {formData.bank_info.ifsc_code}
                  </div>
                </CardContent>
              </Card>

              {/* SALARY STRUCTURE */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Salary Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {/* Earnings */}
                  <div>
                    <div className="font-semibold text-green-600 mb-2">
                      Earnings:
                    </div>
                    {selectedComponents
                      .filter((c) => c.category !== "deductions")
                      .map((comp) => (
                        <div
                          key={comp._id}
                          className="flex justify-between ml-2"
                        >
                          <span>{comp.name_in_payslip}:</span>
                          <span>₹{comp.amount.toLocaleString()}</span>
                        </div>
                      ))}
                  </div>

                  {/* Deductions */}
                  {hasFeature("advanced_payroll") && (
                    <div>
                      <div className="font-semibold text-orange-600 mb-2">
                        Deductions:
                      </div>
                      {selectedComponents
                        .filter((c) => c.category === "deductions")
                        .map((comp) => (
                          <div
                            key={comp._id}
                            className="flex justify-between ml-2"
                          >
                            <span>{comp.name_in_payslip}:</span>
                            <span>₹{comp.amount.toLocaleString()}</span>
                          </div>
                        ))}
                    </div>
                  )}

                  <Separator />

                  {/* Salary Summary */}
                  <div className="flex justify-between mt-2">
                    <span className="font-semibold">Gross Salary:</span>
                    <span className="font-semibold">
                      ₹{grossSalary.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold">Total Deductions:</span>
                    <span>₹{totalDeductions.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-green-700">
                    <span>Net Salary:</span>
                    <span>₹{netSalary.toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="add-employee-form">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate("/employees")}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Add New Employee
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Create a new employee record with complete details
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                      isCompleted
                        ? "bg-primary border-primary text-primary-foreground"
                        : isActive
                        ? "border-primary text-primary"
                        : "border-gray-300 text-gray-400"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div
                    className={`ml-2 hidden sm:block ${
                      isActive
                        ? "text-primary font-medium"
                        : isCompleted
                        ? "text-primary"
                        : "text-gray-400"
                    }`}
                  >
                    {step.title}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-12 h-0.5 mx-4 ${
                        isCompleted ? "bg-primary" : "bg-gray-300"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <Progress
            value={(currentStep / steps.length) * 100}
            className="mb-6"
          />

          {/* Step Content */}
          <div className="min-h-96">{renderStepContent()}</div>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 1}
            >
              Previous
            </Button>

            <div className="flex space-x-2">
              {currentStep < steps.length ? (
                <Button
                  onClick={nextStep}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="next-step-btn"
                >
                  Next Step
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="submit-employee-btn"
                >
                  {loading ? "Creating Employee..." : "Create Employee"}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddEmployee;
